<template>
  <div class="job-card mb-2" @click="$emit('click', job)">
    <!-- Desktop layout: single row -->
    <div class="desktop-layout">
      <div class="job-card-info">
        <div class="company-name">{{ companyName || job.companyName }}</div>
        <div class="job-title" v-html="highlightText(job.job_title)"></div>
      </div>
      <div class="scout-date" v-if="isJobScouted && formattedScoutDate">スカウト希望日 {{ formattedScoutDate }}</div>
      <div v-if="isJobScouted" class="scouted-badge">
        <div :class="['scouted-badge-text', isExpired ? 'expired' : 'active']">
          {{ isExpired ? 'スカウト希望期間終了' : 'スカウト希望中' }}
        </div>
      </div>
      <div v-else class="job-actions">
        <button type="button" class="btn-view-detail">詳しく見る</button>
        <button
          type="button"
          class="btn-scout-wish"
          :disabled="scoutDisabled"
          data-testid="job-scout-button"
          @click.stop="$emit('scout', job)"
        >
          <img src="@/assets/images/ticket-badge.svg" alt="" class="scout-wish-icon" width="26" height="10" />
          スカウト希望
        </button>
      </div>
    </div>

    <!-- Mobile layout: 3 rows -->
    <div class="mobile-layout">
      <!-- Line 1: Company name -->
      <div class="mobile-row-1">
        <div class="company-name">{{ companyName || job.companyName }}</div>
      </div>
      
      <!-- Line 2: Job title -->
      <div class="mobile-row-2">
        <div class="job-title-container">
          <div class="job-title" v-html="highlightText(job.job_title)"></div>
        </div>
      </div>
      
      <!-- Line 3: Scout date (left) and Scout badge (right) -->
      <div
        v-if="showMobileScoutRow"
        class="mobile-row-3 mt-2 d-flex justify-content-between align-items-center gap-5"
      >
        <div class="scout-date" v-if="isJobScouted && formattedScoutDate">スカウト希望日 {{ formattedScoutDate }}</div>
        <div v-if="isJobScouted || (!isJobScouted && isProfileSharedCompany)" class="scouted-badge">
          <div v-if="isJobScouted" :class="['scouted-badge-text', isExpired ? 'expired' : 'active']">
            {{ isExpired ? 'スカウト希望期間終了' : 'スカウト希望中' }}
          </div>
        </div>
      </div>
      <div v-else class="mobile-row-3 mt-2 job-actions">
        <button type="button" class="btn-view-detail">詳しく見る</button>
        <button
          type="button"
          class="btn-scout-wish"
          :disabled="scoutDisabled"
          data-testid="job-scout-button-mobile"
          @click.stop="$emit('scout', job)"
        >
          <img src="@/assets/images/ticket.svg" alt="" class="scout-wish-icon" width="16" height="16" />
          スカウト希望
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { highlightText } from "@/utils/textHighlight";
import { mapGetters } from "vuex";

export default {
  name: "JobListItem",
  props: {
    job: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    isJobScouted: {
      type: Boolean,
      required: true,
    },
    companyName: {
      type: String,
      required: false,
    },
    searchKeyword: {
      type: String,
      default: '',
    },
    scoutDisabled: {
      type: Boolean,
      default: false,
    }
  },
  computed: {
    ...mapGetters({
      profile: 'getProfile',
    }),
    isProfileSharedCompany() {
      const companyId = this.job.company_id || this.job.companyId;
      return this.profile && this.profile.other_companies && companyId && this.profile.other_companies.includes(companyId);
    },
    isExpired() {
      return this.job.status === 'expired';
    },
    formattedScoutDate() {
      if (!this.job.scoutedAt) return '';
      return moment(this.job.scoutedAt).format('MM/DD');
    },
    showMobileScoutRow() {
      return this.isJobScouted;
    },
  },
  methods: {
    highlightText(text) {
      return highlightText(text, this.searchKeyword);
    },
  },
};
</script>

<style scoped>
.job-card {
  background-color: white;
  border: 1px solid #e0e0e0;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
  border-radius: 0px 4px 4px 0px #00000040;
  padding: 5px 53px;
}

.job-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* Desktop Layout (default) */
.desktop-layout {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  width: 100%;
}

.mobile-layout {
  display: none;
}

.job-card-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  min-width: 0;
}

.company-name {
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.scout-date {
  font-size: 12px;
  color: #3c3c3c;
  white-space: nowrap;
}

.job-title-container {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.job-title {
  font-size: 16px;
  font-weight: 700;
  color: #000000;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  display: block;
}

.gap-5 {
  gap: 5px !important;
}
/* Mobile Layout */
@media (max-width: 767px) {
  .job-card {
    height: auto;
    padding: 9px 10px;
    border: 1px solid #cccccc;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    gap: 7px;
    box-shadow: 0px 4px 4px 0px #00000040;
    margin-bottom: 12px !important;
  }

  .desktop-layout {
    display: none;
  }

  .mobile-layout {
    display: flex;
    flex-direction: column;
    gap: 10px;
    height: auto;
  }

  .mobile-row-1,
  .mobile-row-2,
  .mobile-row-3 {
    display: flex;
    align-items: center;
  }

  .mobile-row-1 {
    flex-shrink: 0;
    min-height: 0;
    overflow: hidden;
    width: 100%;
    max-width: 100%;
    align-items: flex-start;
  }

  .mobile-row-2 {
    flex: 1;
    min-height: 0;
  }

  .mobile-row-2 .job-title-container {
    width: 100%;
  }

  .mobile-row-3 {
    flex-shrink: 0;
  }

  .mobile-layout .company-name {
    font-family: Roboto, sans-serif;
    font-size: 11px;
    font-weight: 400;
    line-height: 100%;
    letter-spacing: 0%;
    vertical-align: middle;
    color: #1a1a1a;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: break-all;
    width: 100%;
    max-width: 100%;
  }

  .mobile-layout .job-title {
    font-family: Roboto, sans-serif;
    font-size: 14px;
    font-weight: 700;
    line-height: 100%;
    letter-spacing: 0%;
    vertical-align: middle;
    color: #1a1a1a;
  }

  .mobile-layout .scout-date {
    font-size: 12px;
    color: #3c3c3c;
  }

  .scouted-badge-text {
    min-width: 140px !important;
    width: auto !important;
  }
}

.scouted-badge {
  flex-shrink: 0;
  margin-left: auto;
}

.job-actions {
  flex-shrink: 0;
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-view-detail {
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0.15);
  background: #ffffff;
  border: 1px solid #cccccc;
  border-radius: 20px;
  color: #3c3c3c;
  font-size: 12px;
  font-weight: 600;
  padding: 8px 16px;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.btn-view-detail:hover {
  background: #f0f0f0;
  border-color: #b3b3b3;
}

.btn-view-detail:active {
  background: #e0e0e0;
}

.btn-scout-wish {
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 6px;
  background: #006c6b;
  border: 1px solid #006c6b;
  border-radius: 20px;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  padding: 8px 16px;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.btn-scout-wish:hover:not(:disabled) {
  background: #005453;
  border-color: #005453;
}

.btn-scout-wish:active:not(:disabled) {
  background: #003f3e;
  border-color: #003f3e;
}

.btn-scout-wish:disabled {
  background: #c4c4c4;
  border-color: #c4c4c4;
  cursor: not-allowed;
}

.scout-wish-icon {
  display: block;
  flex-shrink: 0;
  filter: brightness(0) invert(1);
}

@media (max-width: 767px) {
  .job-actions {
    width: 100%;
    gap: 13px;
  }

  .btn-view-detail {
    display: flex;
    align-items: center;
    flex: 1 1 0;
    min-width: 0;
    height: 30px;
    border-radius: 4px;
    padding: 0;
    justify-content: center;
  }

  .btn-scout-wish {
    flex: 1 1 0;
    min-width: 0;
    height: 30px;
    border-radius: 4px;
    padding: 0;
    justify-content: center;
  }
}

/* Desktop Layout */
@media (min-width: 768px) {
  .job-card {
    border: 1px solid #a0a0a0;
    border-radius: 4px;
    padding: 7px 15px;
    height: 64px;
    box-shadow: none;
    margin-bottom: 8px !important;
  }

  .mobile-layout {
    display: none;
  }

  .scout-date {
    margin-left: 15px;
  }

  .scouted-badge-text {
    min-width: 140px;
  }

  .btn-view-detail {
    border: 1px solid #7b7b7b;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 400;
    width: 126px;
    height: 32px;
    padding: 0 22px 1px;
  }

  .btn-scout-wish {
    gap: 10px;
    border: 1px solid #a8a8a8;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 700;
    width: 148px;
    height: 34px;
    padding: 3px 1px;
    justify-content: center;
  }

  .scout-wish-icon {
    filter: none;
  }
}

.scouted-badge-text {
  min-width: 168px;
  height: 25px;
  font-weight: bold;
  text-align: center;
  font-size: 12px;
  padding: 5px 10px;
  border-radius: 20px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
}

.scouted-badge-text.active {
  background-color: #14B8A6;
  color: #ffffff;
  border: 1px solid #14B8A6;
  width: 168px;
}

.scouted-badge-text.expired {
  background-color: #C4C4C4;
  color: #515C6B;
  border: 1px solid #C4C4C4;
  width: 168px;
}

.scouted-badge-text.profile-shared {
  background-color: #C4C4C4;
  color: #515C6B;
  border: 1px solid #C4C4C4;
  width: 230px;
  font-size: 12px;
}

.job-description {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  word-wrap: break-word;
  white-space: normal;
}

::v-deep .highlight {
  background-color: #ffd700;
  padding: 0 2px;
  border-radius: 2px;
}

@media (min-width: 1024px) {
  .job-title-container {
    max-width: 600px;
  }
}
</style>
